/* Generic library javascript file dealing with rectangles */
import Point from './point.js';

class Rect {
  /**
   * Rectangle constructor. Creates a normalized rectangle.
   * @param {Number} left
   * @param {Number} right
   * @param {Number} top
   * @param {Number} bottom
   */
  constructor(left, right, top, bottom) {
    this.left = left;
    this.right = right;
    this.top = top;
    this.bottom = bottom;
    this.normalize();
  }

  get left() {
    return this.leftValue;
  }

  set left(value) {
    this.leftValue = value;
  }

  get right() {
    return this.rightValue;
  }

  set right(value) {
    this.rightValue = value;
  }

  get top() {
    return this.topValue;
  }

  set top(value) {
    this.topValue = value;
  }

  get bottom() {
    return this.bottomValue;
  }

  set bottom(value) {
    this.bottomValue = value;
  }

  get width() {
    return this.right - this.left;
  }

  set width(value) {
    this.widthValue = value;
  }

  get height() {
    return this.bottom - this.top;
  }

  set height(value) {
    this.heightValue = value;
  }

  /**
   * Get the middle of the rectangle
   * @returns {Point}   The middle point
   */
  get midpoint() {
    return new Point(this.left + this.width / 2, this.top + this.height / 2);
  }

  /**
   * Get the top left point
   * @returns {Point}   The top left
   */
  get topLeft() {
    return new Point(this.left, this.top);
  }

  /**
   * Get the bottom right point
   * @returns {Point}   The bottom right
   */
  get bottomRight() {
    return new Point(this.right, this.bottom);
  }

  /**
   * Convert the Rect to a js object
   */
  toObject() {
    return {
      left: this.left,
      right: this.right,
      top: this.top,
      bottom: this.bottom,
    };
  }

  /**
   * Normalize the current rectangle: make sure left < right and up < down
   */
  normalize() {
    const { left, right, top, bottom } = this;

    this.left = Math.min(left, right);
    this.top = Math.min(top, bottom);
    this.right = Math.max(left, right);
    this.bottom = Math.max(top, bottom);
  }

  /**
   * Check if a given point is in the rectangle
   * @param   {Point}   pt    The point to check
   * @returns {Boolean}       Whether the point is inside the rectangle
   */
  containsPoint(pt) {
    return (
      this.left <= pt.x &&
      pt.x <= this.right &&
      this.top <= pt.y &&
      pt.y <= this.bottom
    );
  }

  /**
   * Check whether the rectangle r1 intersects with the rectangle r2
   * @param   {Rect}    r1
   * @param   {Rect}    r2
   * @returns {boolean}
   */
  static intersectRect(r1, r2) {
    return (
      r1.left <= r2.right &&
      r2.left <= r1.right &&
      r1.top <= r2.bottom &&
      r2.top <= r1.bottom
    );
  }

  /**
   * Check whether the rectangle r2 is fully enclosed by rectangle r1
   * @param   {Rect}    r1
   * @param   {Rect}    r2
   * @returns {boolean}
   */
  static containsRect(r1, r2) {
    return (
      r1.left < r2.left &&
      r1.right > r2.right &&
      r1.top < r2.top &&
      r1.bottom > r2.bottom
    );
  }

  /**
   * Transform rect coordinates to a distance from a point other than (0,0)
   * @param   {Point}   pt    The point of reference
   * @returns {Rect}          The new rectangle
   */
  offsetRect(pt) {
    this.left -= pt.x;
    this.right -= pt.x;
    this.top -= pt.y;
    this.bottom -= pt.y;
  }

  /**
   * Compute the bounding rectangle of an array of rectangles
   * @param   {Array}   rects   The array with the rectangle objects of which to find the bounding rectangle
   * @return  {Rect}            The bounding rectangle
   */
  static boundingRect(rects) {
    if (rects.length === 0) {
      return null;
    }
    return rects.reduce(
      (bRect, rect) => {
        bRect.left = Math.min(bRect.left, rect.left);
        bRect.right = Math.max(bRect.right, rect.right);
        bRect.top = Math.min(bRect.top, rect.top);
        bRect.bottom = Math.max(bRect.bottom, rect.bottom);
        return bRect;
      },
      new Rect(rects[0].left, rects[0].right, rects[0].top, rects[0].bottom)
    );
  }
}

export default Rect;
